import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  Key, Webhook, ArrowLeft, RefreshCw, Plus, Trash2, Copy, Eye, EyeOff,
  Activity, Shield, Clock, AlertCircle, CheckCircle, ExternalLink, Loader2, Code
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WEBHOOK_EVENTS = [
  { type: 'document.created', name: 'Document Created' },
  { type: 'document.updated', name: 'Document Updated' },
  { type: 'document.deleted', name: 'Document Deleted' },
  { type: 'document.approved', name: 'Document Approved' },
  { type: 'document.signed', name: 'Document Signed' },
  { type: 'document.version_uploaded', name: 'New Version Uploaded' },
  { type: 'compliance.assessed', name: 'Compliance Assessed' },
  { type: 'study.created', name: 'Study Created' },
  { type: 'study.updated', name: 'Study Updated' },
  { type: 'alert.generated', name: 'Alert Generated' }
];

export default function APIIntegrationPage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [activeTab, setActiveTab] = useState('api-keys');
  
  // Create API Key Dialog
  const [showCreateKeyDialog, setShowCreateKeyDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScope, setNewKeyScope] = useState('read_only');
  const [newKeyExpiry, setNewKeyExpiry] = useState('365');
  const [newKeyRateLimit, setNewKeyRateLimit] = useState('1000');
  const [createdKey, setCreatedKey] = useState(null);
  const [creating, setCreating] = useState(false);
  
  // Create Webhook Dialog
  const [showCreateWebhookDialog, setShowCreateWebhookDialog] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookDescription, setWebhookDescription] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [createdWebhook, setCreatedWebhook] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { headers } = getAuthHeaders();

      // Fetch API keys
      const keysRes = await axios.get(`${API_URL}/api/api-keys`, { headers });
      setApiKeys(keysRes.data?.api_keys || []);

      // Fetch webhooks
      const webhooksRes = await axios.get(`${API_URL}/api/webhooks`, { headers });
      setWebhooks(webhooksRes.data?.webhooks || []);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load integration data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    setCreating(true);
    try {
      const { headers } = getAuthHeaders();
      const response = await axios.post(`${API_URL}/api/api-keys`, {
        name: newKeyName,
        scope: newKeyScope,
        expires_in_days: parseInt(newKeyExpiry),
        rate_limit: parseInt(newKeyRateLimit)
      }, { headers });

      setCreatedKey(response.data);
      toast.success('API key created successfully');
      await fetchData();
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error(error.response?.data?.detail || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const revokeApiKey = async (keyId) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      const { headers } = getAuthHeaders();
      await axios.delete(`${API_URL}/api/api-keys/${keyId}`, { headers });
      toast.success('API key revoked');
      await fetchData();
    } catch (error) {
      console.error('Error revoking API key:', error);
      toast.error('Failed to revoke API key');
    }
  };

  const createWebhook = async () => {
    if (!webhookUrl.trim()) {
      toast.error('Please enter a webhook URL');
      return;
    }
    if (selectedEvents.length === 0) {
      toast.error('Please select at least one event');
      return;
    }

    setCreating(true);
    try {
      const { headers } = getAuthHeaders();
      const response = await axios.post(`${API_URL}/api/webhooks`, {
        url: webhookUrl,
        events: selectedEvents,
        description: webhookDescription
      }, { headers });

      setCreatedWebhook(response.data);
      toast.success('Webhook created successfully');
      await fetchData();
    } catch (error) {
      console.error('Error creating webhook:', error);
      toast.error(error.response?.data?.detail || 'Failed to create webhook');
    } finally {
      setCreating(false);
    }
  };

  const deleteWebhook = async (webhookId) => {
    if (!confirm('Are you sure you want to delete this webhook?')) {
      return;
    }

    try {
      const { headers } = getAuthHeaders();
      await axios.delete(`${API_URL}/api/webhooks/${webhookId}`, { headers });
      toast.success('Webhook deleted');
      await fetchData();
    } catch (error) {
      console.error('Error deleting webhook:', error);
      toast.error('Failed to delete webhook');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleEvent = (eventType) => {
    setSelectedEvents(prev => 
      prev.includes(eventType) 
        ? prev.filter(e => e !== eventType)
        : [...prev, eventType]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading integration settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-700/50 bg-gradient-to-r from-slate-900 to-slate-800 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard?view=all')}
                className="text-white/70 hover:text-white hover:bg-slate-50/10"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-50/20"></div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                  <Code className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">API Integration</h1>
                  <p className="text-xs text-white/60">Manage API Keys & Webhooks</p>
                </div>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              className="border-white/20 text-white hover:bg-slate-50/10"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100/80 border border-slate-300 mb-6">
            <TabsTrigger value="api-keys" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="tab-api-keys">
              <Key className="w-4 h-4 mr-2" />
              API Keys ({apiKeys.length})
            </TabsTrigger>
            <TabsTrigger value="webhooks" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="tab-webhooks">
              <Webhook className="w-4 h-4 mr-2" />
              Webhooks ({webhooks.length})
            </TabsTrigger>
            <TabsTrigger value="docs" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="tab-docs">
              <ExternalLink className="w-4 h-4 mr-2" />
              API Documentation
            </TabsTrigger>
          </TabsList>

          {/* API Keys Tab */}
          <TabsContent value="api-keys" className="space-y-4" data-testid="api-keys-content">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">API Keys</h2>
                <p className="text-sm text-slate-600">Manage API keys for external integrations</p>
              </div>
              <Button onClick={() => { setShowCreateKeyDialog(true); setCreatedKey(null); }} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                Create API Key
              </Button>
            </div>

            {apiKeys.length === 0 ? (
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-12 text-center">
                  <Key className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No API Keys</h3>
                  <p className="text-slate-600 mb-4">Create an API key to enable external integrations</p>
                  <Button onClick={() => setShowCreateKeyDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First API Key
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {apiKeys.map((key) => (
                  <Card key={key.id} className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${key.is_active ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                            <Key className={`w-5 h-5 ${key.is_active ? 'text-green-600' : 'text-red-600'}`} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">{key.name}</h4>
                            <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                              <code className="bg-slate-200 px-2 py-0.5 rounded text-xs">{key.key_prefix}...</code>
                              <Badge className={key.scope === 'read_only' ? 'bg-blue-500/20 text-blue-600' : key.scope === 'read_write' ? 'bg-amber-500/20 text-amber-600' : 'bg-red-500/20 text-red-600'}>
                                {key.scope?.replace('_', ' ')}
                              </Badge>
                              <span>Rate: {key.rate_limit}/hr</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right text-sm">
                            <p className="text-slate-600">Created: {key.created_at ? format(parseISO(key.created_at), 'MMM dd, yyyy') : 'N/A'}</p>
                            {key.last_used_at && (
                              <p className="text-slate-500">Last used: {format(parseISO(key.last_used_at), 'MMM dd, HH:mm')}</p>
                            )}
                            <p className="text-slate-500">Requests: {key.requests_count || 0}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => revokeApiKey(key.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Webhooks Tab */}
          <TabsContent value="webhooks" className="space-y-4" data-testid="webhooks-content">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Webhooks</h2>
                <p className="text-sm text-slate-600">Receive real-time notifications for events</p>
              </div>
              <Button onClick={() => { setShowCreateWebhookDialog(true); setCreatedWebhook(null); setSelectedEvents([]); }} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Webhook
              </Button>
            </div>

            {webhooks.length === 0 ? (
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-12 text-center">
                  <Webhook className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Webhooks</h3>
                  <p className="text-slate-600 mb-4">Create a webhook to receive real-time event notifications</p>
                  <Button onClick={() => setShowCreateWebhookDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Webhook
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {webhooks.map((webhook) => (
                  <Card key={webhook.id} className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${webhook.is_active ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                            <Webhook className={`w-5 h-5 ${webhook.is_active ? 'text-green-600' : 'text-red-600'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 break-all">{webhook.url}</p>
                            {webhook.description && <p className="text-sm text-slate-500">{webhook.description}</p>}
                            <div className="flex flex-wrap gap-1 mt-2">
                              {webhook.events?.map((event, idx) => (
                                <Badge key={idx} className="bg-purple-100 text-purple-700 text-xs">{event}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right text-sm">
                            <p className="text-slate-600">Created: {webhook.created_at ? format(parseISO(webhook.created_at), 'MMM dd, yyyy') : 'N/A'}</p>
                            {webhook.failure_count > 0 && (
                              <p className="text-red-600">Failures: {webhook.failure_count}</p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteWebhook(webhook.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* API Docs Tab */}
          <TabsContent value="docs" className="space-y-4" data-testid="docs-content">
            <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-slate-900">Public API Documentation</CardTitle>
                <CardDescription>Use these endpoints with your API key</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Base URL</h3>
                  <code className="block bg-slate-200 p-3 rounded text-sm">{API_URL}/api/v1</code>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Authentication</h3>
                  <p className="text-slate-600 mb-2">Include your API key in the request header:</p>
                  <code className="block bg-slate-200 p-3 rounded text-sm">X-API-Key: your_api_key_here</code>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Available Endpoints</h3>
                  <div className="space-y-3">
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-green-500 text-white">GET</Badge>
                        <code className="text-sm">/v1/documents</code>
                      </div>
                      <p className="text-sm text-slate-600">List all documents. Supports query params: study_id, status, document_type, limit, offset</p>
                    </div>
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-green-500 text-white">GET</Badge>
                        <code className="text-sm">/v1/documents/:id</code>
                      </div>
                      <p className="text-sm text-slate-600">Get a single document by ID</p>
                    </div>
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-green-500 text-white">GET</Badge>
                        <code className="text-sm">/v1/studies</code>
                      </div>
                      <p className="text-sm text-slate-600">List all studies. Supports query params: status, limit, offset</p>
                    </div>
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-green-500 text-white">GET</Badge>
                        <code className="text-sm">/v1/studies/:id</code>
                      </div>
                      <p className="text-sm text-slate-600">Get a single study by ID with document count</p>
                    </div>
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-green-500 text-white">GET</Badge>
                        <code className="text-sm">/v1/studies/:id/compliance</code>
                      </div>
                      <p className="text-sm text-slate-600">Get compliance summary for a study</p>
                    </div>
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-green-500 text-white">GET</Badge>
                        <code className="text-sm">/v1/studies/:id/tmf-health</code>
                      </div>
                      <p className="text-sm text-slate-600">Get TMF health analysis for a study</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Rate Limiting</h3>
                  <p className="text-slate-600">API keys have a configurable rate limit (default: 1000 requests/hour). Exceeded limits return HTTP 429.</p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Example Request</h3>
                  <pre className="bg-slate-800 text-green-400 p-4 rounded-lg text-sm overflow-x-auto">
{`curl -X GET "${API_URL}/api/v1/studies" \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json"`}
                  </pre>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create API Key Dialog */}
      <Dialog open={showCreateKeyDialog} onOpenChange={setShowCreateKeyDialog}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-purple-600" />
              {createdKey ? 'API Key Created' : 'Create API Key'}
            </DialogTitle>
            <DialogDescription>
              {createdKey ? 'Save your API key securely - it won\'t be shown again.' : 'Create a new API key for external integrations.'}
            </DialogDescription>
          </DialogHeader>

          {createdKey ? (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-800">{createdKey.warning}</p>
                </div>
              </div>
              <div>
                <Label className="text-slate-600">Your API Key</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 bg-slate-100 px-3 py-2 rounded text-sm break-all">{createdKey.api_key}</code>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(createdKey.api_key)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => { setShowCreateKeyDialog(false); setCreatedKey(null); }}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="key-name">Name</Label>
                <Input
                  id="key-name"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Production Integration"
                />
              </div>
              <div>
                <Label htmlFor="key-scope">Scope</Label>
                <Select value={newKeyScope} onValueChange={setNewKeyScope}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="read_only">Read Only</SelectItem>
                    <SelectItem value="read_write">Read & Write</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="key-expiry">Expires In (days)</Label>
                  <Input
                    id="key-expiry"
                    type="number"
                    value={newKeyExpiry}
                    onChange={(e) => setNewKeyExpiry(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="key-rate">Rate Limit (req/hr)</Label>
                  <Input
                    id="key-rate"
                    type="number"
                    value={newKeyRateLimit}
                    onChange={(e) => setNewKeyRateLimit(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateKeyDialog(false)}>Cancel</Button>
                <Button onClick={createApiKey} disabled={creating} className="bg-purple-600 hover:bg-purple-700">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Webhook Dialog */}
      <Dialog open={showCreateWebhookDialog} onOpenChange={setShowCreateWebhookDialog}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Webhook className="w-5 h-5 text-purple-600" />
              {createdWebhook ? 'Webhook Created' : 'Create Webhook'}
            </DialogTitle>
            <DialogDescription>
              {createdWebhook ? 'Your webhook has been created. Use the secret to verify incoming requests.' : 'Subscribe to events and receive real-time notifications.'}
            </DialogDescription>
          </DialogHeader>

          {createdWebhook ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <p className="text-sm text-green-800">Webhook created successfully!</p>
                </div>
              </div>
              <div>
                <Label className="text-slate-600">Webhook Secret</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 bg-slate-100 px-3 py-2 rounded text-sm break-all">{createdWebhook.secret}</code>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(createdWebhook.secret)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Use this secret to verify webhook signatures</p>
              </div>
              <DialogFooter>
                <Button onClick={() => { setShowCreateWebhookDialog(false); setCreatedWebhook(null); }}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="webhook-url">Webhook URL</Label>
                <Input
                  id="webhook-url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://your-server.com/webhook"
                />
              </div>
              <div>
                <Label htmlFor="webhook-desc">Description (optional)</Label>
                <Input
                  id="webhook-desc"
                  value={webhookDescription}
                  onChange={(e) => setWebhookDescription(e.target.value)}
                  placeholder="e.g., Production notifications"
                />
              </div>
              <div>
                <Label className="mb-2 block">Events to Subscribe</Label>
                <ScrollArea className="h-48 border rounded-lg p-3">
                  <div className="space-y-2">
                    {WEBHOOK_EVENTS.map((event) => (
                      <div key={event.type} className="flex items-center gap-2">
                        <Checkbox
                          id={event.type}
                          checked={selectedEvents.includes(event.type)}
                          onCheckedChange={() => toggleEvent(event.type)}
                        />
                        <Label htmlFor={event.type} className="text-sm cursor-pointer">{event.name}</Label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateWebhookDialog(false)}>Cancel</Button>
                <Button onClick={createWebhook} disabled={creating} className="bg-purple-600 hover:bg-purple-700">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
